import { subject } from '@casl/ability';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
} from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputField } from 'src/components/atoms/input-field';
import PhoneNumberInput from 'src/components/atoms/phone-number-input/phone-number-input';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { usePrompt } from 'src/hooks/use-prompt';
import { usePermissions } from 'src/permissions/can';
import { UserRole } from 'src/types/enum/user-role';
import { z } from 'zod';
import { useInviteAdmin } from '../../../hooks/api/use-invite-admin';

interface FormValues {
  email: string;
  phoneNumber: string;
  role: UserRole;
}

interface Props extends DialogActions {
  adminToken: string;
  adminRole: UserRole;
}

const InviteAdminDialog = ({
  open,
  onCancel,
  adminRole,
  adminToken,
}: Props) => {
  const { t } = useTranslation();
  const permissions = usePermissions();

  const defaultValues = {
    email: '',
    phoneNumber: '',
    role: UserRole.OWNER,
  };

  const validationSchema = z
    .object({
      email: z
        .string()
        .email(t('validation:email'))
        .min(1, t('validation:required')),
      role: z.nativeEnum(UserRole),
      phoneNumber: z.string(),
    })
    .refine(value =>
      permissions.can(
        'create',
        subject('TenantAdministrators', {
          userToken: adminToken,
          role: adminRole,
          selectedRole: value.role,
        }),
      ),
    );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
    defaultValues,
  });

  usePrompt(isDirty);

  const handleOnCancel = () => {
    onCancel?.();
    reset(defaultValues);
  };

  const { mutate: inviteAdmin, isPending: isLoadingTenantAdmin } =
    useInviteAdmin(handleOnCancel);

  const onSubmit: SubmitHandler<FormValues> = data => {
    inviteAdmin({
      ...data,
      phoneNumber: data.phoneNumber ? `+${data.phoneNumber}` : undefined,
    });
  };

  const roleOptions = [
    {
      label: t('common:roles.owner'),
      description: 'organizationDetails:admins.dialog.ownerDescription',
      value: UserRole.OWNER,
      disabled: permissions.cannot(
        'create',
        subject('TenantAdministrators', {
          userToken: adminToken,
          role: adminRole,
          selectedRole: UserRole.OWNER,
        }),
      ),
    },
    {
      label: t('common:roles.admin'),
      description: 'organizationDetails:admins.dialog.adminDescription',
      value: UserRole.ADMIN,
      disabled: permissions.cannot(
        'create',
        subject('TenantAdministrators', {
          userToken: adminToken,
          role: adminRole,
          selectedRole: UserRole.ADMIN,
        }),
      ),
    },
    {
      label: t('common:roles.viewer'),
      description: 'organizationDetails:admins.dialog.viewerDescription',
      value: UserRole.VIEWER,
      disabled: permissions.cannot(
        'create',
        subject('TenantAdministrators', {
          userToken: adminToken,
          role: adminRole,
          selectedRole: UserRole.VIEWER,
        }),
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      title={t('organizationDetails:admins.dialogTitle')}
      onCancel={handleOnCancel}
      onConfirm={handleSubmit(onSubmit)}
      confirmButtonText={t('common:button.invite')}
      cancelButtonText={t('common:button.cancel')}
      confirmButtonId="invite_administrator_confirm_button"
      isConfirmButtonSubmitting={isLoadingTenantAdmin}
      isConfirmButtonDisabled={!isValid}>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Controller
          name="email"
          control={control}
          render={({ field: { onChange, value, onBlur }, fieldState }) => (
            <InputField
              fullWidth
              sx={{ my: 1 }}
              name="email"
              label={t('organizationDetails:admins.email')}
              value={value}
              onBlur={onBlur}
              onChange={onChange}
              required
              error={Boolean(fieldState.invalid)}
              helperText={fieldState.error?.message}
              id="invite_administrator_email_input"
            />
          )}
        />
        <Box sx={{ width: '100%' }}>
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field: { onChange, value, onBlur }, fieldState }) => (
              <PhoneNumberInput
                label={t('organizationDetails:admins.phoneNumber')}
                onBlur={onBlur}
                onChange={onChange}
                value={value}
                error={Boolean(fieldState.invalid && fieldState.isTouched)}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Box>
      </Box>
      <Controller
        name="role"
        render={({ field: { onChange, value, onBlur } }) => (
          <RadioGroup
            sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}
            defaultValue={UserRole.OWNER}
            onChange={onChange}
            value={value}
            onBlur={onBlur}>
            {roleOptions.map(option => (
              <Box key={option.value}>
                <FormControlLabel
                  label={t(option.label)}
                  control={<Radio value={option.value} />}
                  disabled={option.disabled}
                />
                <FormHelperText>{t(option.description)}</FormHelperText>
              </Box>
            ))}
          </RadioGroup>
        )}
        control={control}
      />
    </Dialog>
  );
};

export default InviteAdminDialog;
